import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";

function EditVisit() {
  const { visitId } = useParams();
  const navigate = useNavigate();

  /* ---------------- STATE ---------------- */
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("");
  const [notes, setNotes] = useState("");

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);


  /* ---------------- FETCH VISIT ---------------- */
  useEffect(() => {
    fetchVisit();
  }, []);

  const fetchVisit = async () => {
    try {
      const res = await api.get(`/api/visits/single/${visitId}`);
setVisitDate(
  res.data.visit_date
    ? res.data.visit_date.split("T")[0]
    : ""
);      setVisitTime(res.data.visit_time);
      setNotes(res.data.notes || "");

      const imgRes = await api.get(`/api/visit-images/${visitId}`);
      setExistingImages(imgRes.data);
    } catch (err) {
      console.error("Fetch visit failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- VALIDATION (ON SUBMIT ONLY) ---------------- */
  const validate = () => {
    const newErrors = {};

    if (!visitDate) newErrors.visitDate = "Visit date is required";
    if (!visitTime) newErrors.visitTime = "Visit time is required";

    if (notes.length > 1000) {
      newErrors.notes = "Notes cannot exceed 1000 characters";
    }

    if (newImages.length > 10) {
      newErrors.images = "Maximum 10 images allowed";
    }

    for (const file of newImages) {
      if (!file.type.startsWith("image/")) {
        newErrors.images = "Only image files are allowed";
        break;
      }
      if (file.size > 5 * 1024 * 1024) {
        newErrors.images = "Each image must be under 5MB";
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------------- SAVE ---------------- */
  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      // 1️⃣ Update visit fields
      await api.put(`/api/visits/${visitId}`, {
        visit_date: visitDate,
        visit_time: visitTime,
        notes,
      });

      // 2️⃣ Upload new images if any
      if (newImages.length > 0) {
        const formData = new FormData();
        newImages.forEach((img) => formData.append("images", img));

        await api.post(
          `/api/visit-images/${visitId}/prescription`,
          formData
        );
      }

      navigate(-1);
    } catch (err) {
      console.error("Update visit failed", err);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- RENDER ---------------- */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-4">
      <button onClick={() => navigate(-1)} className="text-blue-600 mb-4">
        ← Back
      </button>

      <h1 className="text-xl font-semibold mb-4">Edit Visit</h1>

      {/* DATE */}
      <input
        type="date"
        value={visitDate}
        onChange={(e) => setVisitDate(e.target.value)}
        className="w-full h-11 border rounded px-3 mb-1"
      />
      {errors.visitDate && (
        <p className="text-xs text-red-600 mb-2">
          {errors.visitDate}
        </p>
      )}

      {/* TIME */}
      <input
        type="time"
        value={visitTime}
        onChange={(e) => setVisitTime(e.target.value)}
        className="w-full h-11 border rounded px-3 mb-1"
      />
      {errors.visitTime && (
        <p className="text-xs text-red-600 mb-2">
          {errors.visitTime}
        </p>
      )}

      {/* NOTES */}
      <textarea
        rows={4}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        className="w-full border rounded p-3 mb-1"
      />
      {errors.notes && (
        <p className="text-xs text-red-600 mb-2">
          {errors.notes}
        </p>
      )}

      {/* EXISTING IMAGES */}
      {existingImages.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium mb-2">
            Existing Images
          </p>
          <div className="grid grid-cols-3 gap-2">
            {existingImages.map((img) => (
              <img
                key={img.id}
                src={`https://api.clinic.mrsinghdev.com${img.image_path}`}
                alt="Visit"
                className="w-full h-24 object-cover rounded-lg border"
              />
            ))}
          </div>
        </div>
      )}

      {/* ADD NEW IMAGES */}
      <div className="mb-4">
        <label className="text-sm font-medium block mb-1">
          Add Images
        </label>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => setNewImages([...e.target.files])}
          className="w-full"
        />
        {errors.images && (
          <p className="text-xs text-red-600 mt-1">
            {errors.images}
          </p>
        )}
      </div>

      {/* SAVE */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="
          w-full
          h-12
          bg-green-600
          text-white
          rounded-xl
          font-medium
          disabled:opacity-50
        "
      >
        {saving ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
}

export default EditVisit;
